import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TEMP_CHAT_ASSISTANTS } from "@/lib/constants/tempChatAssistants";
import { cn } from "@/lib/utils";
import useTempChatStore from "@/store/tempChatStore";
import { BarChart3, Bot, Code, GraduationCap, PenTool } from "lucide-react";
import { useState } from "react";

/**
 * Get icon component for assistant
 */
const getAssistantIcon = (iconName) => {
  const iconProps = { className: "h-4 w-4", strokeWidth: 2 };

  switch (iconName) {
    case "Bot":
      return <Bot {...iconProps} />;
    case "Code":
      return <Code {...iconProps} />;
    case "PenTool":
      return <PenTool {...iconProps} />;
    case "BarChart3":
      return <BarChart3 {...iconProps} />;
    case "GraduationCap":
      return <GraduationCap {...iconProps} />;
    default:
      return <Bot {...iconProps} />;
  }
};

/**
 * TempChatAssistantSelector - Selector for pre-defined assistant templates
 * No authentication required, simple persona selection
 */
export function TempChatAssistantSelector({ className }) {
  const [isSelectOpen, setIsSelectOpen] = useState(false);

  const selectedAssistant = useTempChatStore(
    (state) => state.selectedAssistant
  );
  const setSelectedAssistant = useTempChatStore(
    (state) => state.setSelectedAssistant
  );

  const handleAssistantChange = (assistantId) => {
    if (assistantId === "none") {
      setSelectedAssistant(null);
      return;
    }

    const assistant = TEMP_CHAT_ASSISTANTS.find((a) => a.id === assistantId);
    if (assistant) {
      setSelectedAssistant(assistant);
    }
  };

  // Determine current selection
  const currentValue = selectedAssistant?.id || "none";
  const currentIcon = selectedAssistant?.icon || "Bot";

  return (
    <Select
      value={currentValue}
      onValueChange={handleAssistantChange}
      open={isSelectOpen}
      onOpenChange={setIsSelectOpen}
    >
      <SelectTrigger
        className={cn(
          "h-8 w-auto min-w-[140px] max-w-[180px] px-2 rounded-md",
          "bg-transparent",
          "border border-transparent",
          "hover:bg-zinc-100/50 dark:hover:bg-zinc-800/50",
          "transition-all duration-200",
          "focus:outline-none focus:ring-0 focus-visible:ring-0",
          "text-sm font-medium",
          "[&>svg]:hidden", // Hide the default chevron icon
          className
        )}
      >
        <div className="flex items-center gap-1.5">
          <div className="flex-shrink-0 text-muted-foreground">
            {getAssistantIcon(currentIcon)}
          </div>
          <SelectValue>
            <span className="truncate">
              {selectedAssistant?.name || "General Assistant"}
            </span>
          </SelectValue>
        </div>
      </SelectTrigger>
      <SelectContent
        className={cn(
          "bg-white/80 dark:bg-zinc-900/80",
          "backdrop-blur-md",
          "border border-border/50",
          "w-[240px]",
          "rounded-2xl",
          "z-[400]"
        )}
        position="popper"
        sideOffset={4}
      >
        {/* Default/None option */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div>
                <SelectItem
                  value="none"
                  className={cn(
                    "text-foreground",
                    "flex items-center gap-2 w-full",
                    "data-[highlighted]:bg-zinc-100 dark:data-[highlighted]:bg-zinc-800",
                    "data-[state=checked]:bg-zinc-100 dark:data-[state=checked]:bg-zinc-800",
                    "data-[state=checked]:font-medium",
                    "focus:outline-none focus:ring-0"
                  )}
                >
                  <div className="flex items-center gap-2">
                    <div className="text-muted-foreground">
                      {getAssistantIcon("Bot")}
                    </div>
                    <span>General Assistant</span>
                  </div>
                </SelectItem>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="max-w-xs z-[500]">
              <div className="space-y-1">
                <p className="font-medium">General Assistant</p>
                <p className="text-sm text-muted-foreground">
                  Helpful AI for any task
                </p>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Pre-defined assistants */}
        {TEMP_CHAT_ASSISTANTS.filter((a) => a.id !== "general").map(
          (assistant) => (
            <TooltipProvider key={assistant.id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <SelectItem
                      value={assistant.id}
                      className={cn(
                        "text-foreground",
                        "flex items-center gap-2 w-full",
                        "data-[highlighted]:bg-zinc-100 dark:data-[highlighted]:bg-zinc-800",
                        "data-[state=checked]:bg-zinc-100 dark:data-[state=checked]:bg-zinc-800",
                        "data-[state=checked]:font-medium",
                        "focus:outline-none focus:ring-0"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        <div className="text-muted-foreground">
                          {getAssistantIcon(assistant.icon)}
                        </div>
                        <span>{assistant.name}</span>
                      </div>
                    </SelectItem>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-xs z-[500]">
                  <div className="space-y-1">
                    <p className="font-medium">{assistant.name}</p>
                    <p className="text-sm text-muted-foreground">
                      {assistant.description}
                    </p>
                  </div>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )
        )}
      </SelectContent>
    </Select>
  );
}

export default TempChatAssistantSelector;
